import { Routes } from '@angular/router';
import { authGuard } from './auth/auth.guard';
import { adminGuard } from './auth/admin.guard';
import { ownerGuard } from './auth/owner.guard';
import { lifecycleGuard } from './auth/lifecycle.guard';
import { workspaceGuard } from './auth/workspace.guard';
import { featureGateGuard } from './auth/feature-gate.guard';

export const routes: Routes = [
  { path: 'c/:token', loadComponent: () => import('./public-coverage/public-coverage.component').then(m => m.PublicCoverageComponent) },
  { path: 'workspace-not-found', loadComponent: () => import('./workspace-not-found/workspace-not-found.component').then(m => m.WorkspaceNotFoundComponent) },
  { path: 'login', loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent), canActivate: [workspaceGuard] },
  { path: 'signup', loadComponent: () => import('./auth/signup/signup.component').then(m => m.SignupComponent), canActivate: [workspaceGuard] },
  { path: 'verify', loadComponent: () => import('./auth/verify/verify.component').then(m => m.VerifyComponent), canActivate: [workspaceGuard] },
  { path: 'set-password', loadComponent: () => import('./auth/set-password/set-password.component').then(m => m.SetPasswordComponent), canActivate: [workspaceGuard] },
  { path: 'forgot-password', loadComponent: () => import('./auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent), canActivate: [workspaceGuard] },
  { path: 'reset-password', loadComponent: () => import('./auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent), canActivate: [workspaceGuard] },
  { path: 'change-password', loadComponent: () => import('./auth/change-password/change-password.component').then(m => m.ChangePasswordComponent), canActivate: [authGuard] },
  { path: 'auth/sso/complete', loadComponent: () => import('./auth/sso-complete/sso-complete.component').then(m => m.SsoCompleteComponent), canActivate: [workspaceGuard] },
  { path: 'auth/verify-mfa', loadComponent: () => import('./auth/verify-mfa/verify-mfa.component').then(m => m.VerifyMfaComponent), canActivate: [workspaceGuard] },
  // Frozen/purged workspace interstitial — authGuard only, no lifecycleGuard (would loop)
  { path: 'frozen', loadComponent: () => import('./frozen/frozen.component').then(m => m.FrozenComponent), canActivate: [authGuard] },
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  { path: 'dashboard', loadComponent: () => import('./dashboard/dashboard.component').then(m => m.DashboardComponent), canActivate: [authGuard, lifecycleGuard] },
  {
    path: 'clients',
    loadChildren: () => import('./clients/clients.routes').then(m => m.CLIENTS_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'employees',
    loadChildren: () => import('./employees/employees.routes').then(m => m.EMPLOYEES_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'providers',
    loadChildren: () => import('./providers/providers.routes').then(m => m.PROVIDERS_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'case-managers',
    loadChildren: () => import('./case-managers/case-managers.routes').then(m => m.CASE_MANAGERS_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'service-orders',
    loadChildren: () => import('./service-orders/service-orders.routes').then(m => m.SERVICE_ORDERS_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'employee-availability',
    loadChildren: () => import('./employee-availability/employee-availability.routes').then(m => m.AVAILABILITY_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'schedules',
    loadChildren: () => import('./schedules/schedules.routes').then(m => m.SCHEDULES_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'time-slips',
    loadChildren: () => import('./time-slips/time-slips.routes').then(m => m.TIME_SLIPS_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'absences',
    loadChildren: () => import('./absences/absences.routes').then(m => m.ABSENCES_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'employee-absences',
    redirectTo: 'absences',
    pathMatch: 'full',
  },
  {
    path: 'client-absences',
    redirectTo: 'absences',
    pathMatch: 'full',
  },
  {
    path: 'client-remarks',
    loadChildren: () => import('./client-remarks/client-remarks.routes').then(m => m.CLIENT_REMARKS_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'users',
    loadChildren: () => import('./users/users.routes').then(m => m.USERS_ROUTES),
    canActivate: [authGuard, lifecycleGuard, adminGuard],
  },
  {
    path: 'settings',
    loadChildren: () => import('./settings/settings.routes').then(m => m.SETTINGS_ROUTES),
    canActivate: [authGuard, lifecycleGuard, adminGuard],
  },
  {
    path: 'account',
    loadChildren: () => import('./account/account.routes').then(m => m.ACCOUNT_ROUTES),
    canActivate: [authGuard, lifecycleGuard, ownerGuard],
  },
  {
    path: 'reports',
    loadChildren: () => import('./reports/reports.routes').then(m => m.REPORTS_ROUTES),
    canActivate: [authGuard, lifecycleGuard],
  },
  {
    path: 'ai-skills',
    loadChildren: () => import('./ai-skills/ai-skills.routes').then(m => m.AI_SKILLS_ROUTES),
    canActivate: [authGuard, lifecycleGuard, featureGateGuard],
    data: { feature: 'ai_skills' },
  },
  {
    path: 'shift-broadcasts',
    loadChildren: () => import('./shift-broadcasts/shift-broadcasts.routes').then(m => m.SHIFT_BROADCASTS_ROUTES),
    canActivate: [authGuard, lifecycleGuard, featureGateGuard],
    data: { feature: 'shift_broadcast' },
  },
  {
    path: 'audit-log',
    loadChildren: () => import('./audit-logs/audit-logs.routes').then(m => m.AUDIT_LOGS_ROUTES),
    canActivate: [authGuard, lifecycleGuard, adminGuard],
  },
  {
    path: 'profile',
    loadChildren: () => import('./profile/profile.routes').then(m => m.PROFILE_ROUTES),
    canActivate: [authGuard],
  },
  { path: '**', loadComponent: () => import('./not-found/not-found.component').then(m => m.NotFoundComponent) },
];
